import Box from '@app/components/common/Box';
import Button from '@app/components/common/Button';
import Divider from '@app/components/common/Divider';
import Dropdown from '@app/components/common/Dropdown';
import FiltersDropdown from '@app/components/common/FiltersDropdown';
import Grid from '@app/components/common/Grid';
import Icon from '@app/components/common/Icon';
import SearchInput from '@app/components/common/SearchInput';
import { IItemSelection } from '@app/components/common/SelectionModal/GenericSelectionModal/types';
import RolesSelectionModal from '@app/components/common/SelectionModal/RolesSelectionModal';
import ToolTip from '@app/components/common/ToolTip';
import Typography from '@app/components/common/Typography';
import useRootSelector from '@app/hooks/useRootSelector';
import { selectRoles } from '@app/state/selectors/settingsSelectors';
import { selectUsers } from '@app/state/users/usersSelectors';
import { displayFlexRowStyles } from '@app/theme/commonStyles';
import { MQ_MAX_MEDIUM } from '@app/theme/types';
import {
  DateTime,
  EMPLOYEES_SCREEN_FILTERS_INITIAL_STATE,
  EReportPeriod,
  phoneNumberMask,
  rolesDropdownFactory,
  USER_INITIAL_STATE,
} from '@westondev/tableturn-core';
import isEqual from 'lodash/isEqual';
import { mask } from 'node-masker';
import { useCallback, useContext, useMemo, useState } from 'react';
import { WithTranslation } from 'react-i18next';
import { shallowEqual, useSelector } from 'react-redux';
import { EmployeesScreenContext } from '../EmployeesContext';
import TimePeriodToolTip from './TimePeriodToolTip';
import { SomePeriods, TIME_PERIOD_TEXT } from './TimePeriodToolTip/constants';
import {
  EMPLOYEE_STATUS_TO_VALUE,
  VALUE_TO_EMPLOYEE_STATUS,
} from './constants';

const TopBar = ({ t }: WithTranslation) => {
  const { setFilters, filters, detailsFilters, user } = useContext(
    EmployeesScreenContext,
  );

  const roles = useSelector(selectRoles);
  const userFromSettingsData = useRootSelector(
    state => (user ? selectUsers(state)[user?.orgUserId] : USER_INITIAL_STATE),
    shallowEqual,
  );

  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [isTimePeriodOpen, setIsTimePeriodOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [isRolesModalOpen, setIsRolesModalOpen] = useState(false);

  const closeFilters = () => setIsFiltersOpen(false);

  const openFilters = () => setIsFiltersOpen(true);

  const closeTimePeriod = () => setIsTimePeriodOpen(false);

  const openTimePeriod = () => setIsTimePeriodOpen(true);

  const openRolesModal = () => setIsRolesModalOpen(true);
  const closeRolesModal = () => setIsRolesModalOpen(false);

  const isTooltipActive = useMemo(
    () =>
      !isEqual(
        { roleIds: filters.roleIds, currentStatus: filters.currentStatus },
        {
          roleIds: EMPLOYEES_SCREEN_FILTERS_INITIAL_STATE.roleIds,
          currentStatus: EMPLOYEES_SCREEN_FILTERS_INITIAL_STATE.currentStatus,
        },
      ),
    [filters],
  );

  const rolesDropdownOptions = useMemo(
    () => rolesDropdownFactory(roles),
    [roles],
  );

  const dropdownPlaceholder = useMemo(() => {
    const startDate = user ? detailsFilters.startDate : filters.startDate;
    const endDate = user ? detailsFilters.endDate : filters.endDate;

    if (!startDate) {
      return '';
    }

    const startDateFormatted = DateTime.fromFormat(
      startDate,
      'yyyy/MM/dd',
    ).toFormat('MM/dd/yy');

    const endDateFormatted = endDate
      ? ` - ${DateTime.fromFormat(endDate, 'yyyy/MM/dd').toFormat('MM/dd/yy')}`
      : '';

    const period = user ? detailsFilters.period : filters.period;
    const periodText = t(TIME_PERIOD_TEXT[period as SomePeriods]);

    if (period === EReportPeriod.TODAY) {
      return `${periodText}: ${startDateFormatted}`;
    }

    return `${periodText}: ${startDateFormatted}${endDateFormatted}`;
  }, [
    filters.startDate,
    filters.endDate,
    filters.period,
    detailsFilters.startDate,
    detailsFilters.endDate,
    detailsFilters.period,
    user,
    t,
  ]);

  const handleOnDebounce = useCallback(
    (newText: string) => {
      setFilters({ search: newText });
    },
    [setFilters],
  );

  const rolesButtons = useMemo(
    () =>
      rolesDropdownOptions.map(
        roleDropdownOption =>
          ({
            id: roleDropdownOption.value,
            title: roleDropdownOption.label,
          }) as IItemSelection,
      ),
    [rolesDropdownOptions],
  );

  const statusDropdownOptions = useMemo(
    () => [
      {
        label: t('managementModule.employeesScreen.topBar.filters.status.all'),
        value: 1,
      },
      {
        label: t(
          'managementModule.employeesScreen.topBar.filters.status.clockedIn',
        ),
        value: 2,
      },
      {
        label: t(
          'managementModule.employeesScreen.topBar.filters.status.clockedOut',
        ),
        value: 3,
      },
    ],
    [t],
  );

  const currentStatusDropdownValue = useMemo(() => {
    if (filters.currentStatus === '') {
      return 1;
    }

    return EMPLOYEE_STATUS_TO_VALUE[filters.currentStatus] || 0;
  }, [filters.currentStatus]);

  const handleOnAssociate = (newRoles: IItemSelection[]) => {
    const roleIds = newRoles.map(role => Number(role.id));
    setFilters({ roleIds });
    closeRolesModal();
  };

  const renderTimePeriod = (
    <ToolTip
      isToolTipActive={isTimePeriodOpen}
      onClickOutside={closeTimePeriod}
      mode="click"
      direction="bottom-left"
      dismissOnContentClick={false}
      dismissOnClickOutside={false}
      content={<TimePeriodToolTip />}>
      <Dropdown
        placeholder={dropdownPlaceholder}
        customTextColor="black"
        value={0}
        data={[[]]}
        onChange={() => null}
        isEditable={false}
        onActive={openTimePeriod}
        icon={<Icon name="FaRegCalendar" size="20px" />}
      />
    </ToolTip>
  );

  return (
    <>
      <RolesSelectionModal
        buttons={rolesButtons}
        onAssociate={handleOnAssociate}
        onModalClose={closeRolesModal}
        active={isRolesModalOpen}
        allowAssociateMultiple
        showDescriptionPanel={false}
        btnSuccessText={t('commonButtons.associate')}
        showSelectedButtons
        showCommonFilter={false}
        canAssociateEmptyButtons
      />
      {user ? (
        <Box
          csx={[
            displayFlexRowStyles,
            { gap: 10, justifyContent: 'space-between' },
          ]}>
          <Box csx={{ flex: 1 }}>
            <Typography variant="heading" align="center">
              {user.name}
            </Typography>
          </Box>
          <Divider
            direction="vertical"
            csx={{ height: '100%', maxHeight: 50 }}
          />
          <Box csx={{ flex: 1 }}>
            <Typography align="center">
              {t('managementModule.employeesScreen.employeesTable.pin')}:{' '}
              {user.pin}
            </Typography>
          </Box>
          <Divider
            direction="vertical"
            csx={{ height: '100%', maxHeight: 50 }}
          />
          <Box csx={{ flex: 1 }}>
            <Typography align="center">
              {t('commonTexts.phone')}:{' '}
              {mask(userFromSettingsData.user.phoneNumber, phoneNumberMask) ||
                '--'}
            </Typography>
          </Box>
          <Divider
            direction="vertical"
            csx={{ height: '100%', maxHeight: 50 }}
          />
          <Box csx={{ flex: 1 }}>
            <Typography align="center">
              {t('commonTexts.email')}:{' '}
              {userFromSettingsData.user.email || '--'}
            </Typography>
          </Box>
          <Divider
            direction="vertical"
            csx={{ height: '100%', maxHeight: 50 }}
          />
          <Box csx={{ flex: 1 }}>
            <Typography align="center">
              {t(
                'managementModule.employeesScreen.employeeDetailsTable.payRollId',
              )}
              : {userFromSettingsData.payrollId || '--'}
            </Typography>
          </Box>

          <Divider
            direction="vertical"
            csx={{
              height: '100%',
              maxHeight: 50,
            }}
          />
          <Box csx={{ flex: 1 }}>{renderTimePeriod}</Box>
        </Box>
      ) : (
        <Grid columnGap={10} rowGap={10}>
          <Grid.Item mb={10} lg={3} xl={5}>
            <SearchInput
              onChange={setInputValue}
              debounceTimeout={500}
              onDebounce={handleOnDebounce}
              value={inputValue}
            />
          </Grid.Item>
          <Grid.Item mb={2} lg={1}>
            <Box csx={[displayFlexRowStyles, { gap: 10 }]}>
              <Box csx={{ flexGrow: 1, flexShrink: 1 }}>
                <ToolTip
                  isToolTipActive={isFiltersOpen}
                  onClickOutside={closeFilters}
                  mode="click"
                  dismissOnContentClick={false}
                  dismissOnClickOutside={false}
                  direction="bottom"
                  content={
                    <FiltersDropdown
                      variant="blueTitle"
                      sortValue={null}
                      hideSort
                      data={[
                        {
                          label: t(
                            'managementModule.employeesScreen.topBar.filters.status.title',
                          ),
                          onChange: (value: number | null) => {
                            if (value === null) {
                              return;
                            }
                            if (value === 0) {
                              return setFilters({
                                currentStatus:
                                  EMPLOYEES_SCREEN_FILTERS_INITIAL_STATE.currentStatus,
                              });
                            }
                            const newCurrentStatus =
                              value === 1
                                ? ''
                                : VALUE_TO_EMPLOYEE_STATUS[value];

                            setFilters({
                              currentStatus: newCurrentStatus,
                            });
                          },
                          data: [statusDropdownOptions],
                          value: currentStatusDropdownValue,
                          isMultiSelect: false,
                        },
                        {
                          label: t('menuScreen.common.settings.roles.singular'),
                          onChange: (value: number | null) => {
                            if (value === null) {
                              setFilters({
                                roleIds:
                                  EMPLOYEES_SCREEN_FILTERS_INITIAL_STATE.roleIds,
                              });
                            }
                          },
                          onPress: openRolesModal,
                          data: [rolesDropdownOptions],
                          value: filters.roleIds,
                          isMultiSelect: true,
                          placeholder: t('commonTexts.placeholderDropdown'),
                        },
                      ]}
                      onDone={closeFilters}
                      onSortChange={() => null}
                    />
                  }>
                  <Button
                    variant={
                      isFiltersOpen || isTooltipActive ? 'active' : 'secondary'
                    }
                    csx={{ width: '100%' }}
                    onClick={openFilters}
                    icon={<Icon name="MdFilterAlt" />}
                  />
                </ToolTip>
              </Box>

              <Divider
                direction="vertical"
                csx={{
                  height: '100%',
                  [MQ_MAX_MEDIUM]: {
                    display: 'none',
                  },
                }}
              />
            </Box>
          </Grid.Item>
          <Grid.Item mb={3} md={6} lg={3} xl={2}>
            <Box csx={[displayFlexRowStyles, { gap: 10 }]}>
              <Box csx={{ flexGrow: 1, flexShrink: 1 }}>
                <Button
                  variant={filters.clockedIn ? 'primary' : 'secondary'}
                  csx={{ width: '100%' }}
                  onClick={() =>
                    setFilters({
                      clockedIn: !filters.clockedIn,
                    })
                  }>
                  {t(
                    'managementModule.employeesScreen.topBar.clockedInThisPeriod',
                  )}
                </Button>
              </Box>
              <Divider direction="vertical" csx={{ height: '100%' }} />
            </Box>
          </Grid.Item>
          <Grid.Item mb={9} md={6} lg={5} xl={4}>
            {renderTimePeriod}
          </Grid.Item>
        </Grid>
      )}
    </>
  );
};

export default TopBar;
